import {
  cidItemToString,
  cidRenderItem,
  CidSelectFieldModel,
} from 'components/form/field/select/CidSelectField/CidSelectField'
import { DEFAULT_SELECT_PAGE_PARAM, useAsyncQuerySelect } from 'components/form/field/select/useAsyncQuerySelect'
import { CustomSingleSelectFieldProps, SelectField } from 'components/form/final-form'
import { CidSelectFieldDocument } from 'graphql/hooks.generated'
import { Cid10QueryInput, CidSelectFieldQuery, CidSelectFieldQueryVariables } from 'graphql/types.generated'
import { useMemo } from 'react'
import { CypressProps } from 'types/cypress'
import { emptyArrayOf } from 'util/array'

import { AntecedentesFamiliaresCiapFormModel } from '../model-antecedentes'
import { getBlockedCid10IdsForFamiliarRow } from '../utils-antecedentes'
import { AntecedentesFamiliaresCidSelectItem } from './AntecedentesFamiliaresCidSelectItem'

export type AntecedentesFamiliaresCidSelectFieldProps = CustomSingleSelectFieldProps<CidSelectFieldModel> &
  Pick<Cid10QueryInput, 'somenteRelacionados'> &
  CypressProps & {
    ciapRelacionada?: ID
    currentItemId?: ID
    renderGrupoCiapRelacionada?: boolean
    formValues?: ReadonlyArray<AntecedentesFamiliaresCiapFormModel>
  }

const ID_GRUPO_CIDS_RELACIONADAS = 'grupoCidsRelacionadas'
const ID_GRUPO_OUTRAS_CIDS = 'grupoOutrasCids'

const GRUPO_CIDS_RELACIONADAS_HEADER_ITEM: CidSelectFieldModel = {
  id: ID_GRUPO_CIDS_RELACIONADAS,
  ciapsRelacionadas: [],
  codigo: '',
  nome: '',
}

const GRUPO_OUTRAS_CIDS_HEADER_ITEM: CidSelectFieldModel = {
  id: ID_GRUPO_OUTRAS_CIDS,
  ciapsRelacionadas: [],
  codigo: '',
  nome: '',
}

export function AntecedentesFamiliaresCidSelectField(props: Readonly<AntecedentesFamiliaresCidSelectFieldProps>) {
  const {
    ciapRelacionada,
    currentItemId,
    renderGrupoCiapRelacionada = true,
    somenteRelacionados = false,
    formValues,
    dataTestId,
    itemIsDisabled,
    placeholder = 'Pesquise ou selecione um CID 10',
    ...rest
  } = props

  const {
    selectProps: { items, ...asyncPropsRest },
  } = useAsyncQuerySelect<CidSelectFieldModel, CidSelectFieldQuery, CidSelectFieldQueryVariables>({
    query: CidSelectFieldDocument,
    extractItems: (data) => data?.cids?.content,
    variables: (inputQuery: string): CidSelectFieldQueryVariables => ({
      input: {
        query: inputQuery,
        ciapRelacionada,
        somenteRelacionados,
        pageParams: DEFAULT_SELECT_PAGE_PARAM,
      },
      fetchCiapsRelacionadas: !!ciapRelacionada,
    }),
  })

  const blockedCidIds = useMemo(() => getBlockedCid10IdsForFamiliarRow(formValues, currentItemId, ciapRelacionada), [
    ciapRelacionada,
    currentItemId,
    formValues,
  ])
  const selectItems = useMemo<{
    hasCidsRelacionadas: boolean
    hasOutrasCids: boolean
    items: CidSelectFieldModel[]
  }>(() => {
    const sortedItems = (items ?? emptyArrayOf<CidSelectFieldModel>()).toSorted((a, b) => a.nome.localeCompare(b.nome))

    const cidsRelacionadasComCiap: CidSelectFieldModel[] = renderGrupoCiapRelacionada
      ? [GRUPO_CIDS_RELACIONADAS_HEADER_ITEM]
      : []

    const outrasCid: CidSelectFieldModel[] = renderGrupoCiapRelacionada ? [GRUPO_OUTRAS_CIDS_HEADER_ITEM] : []

    sortedItems.forEach((item) => {
      item.ciapsRelacionadas?.map((value) => value.id).includes(ciapRelacionada)
        ? cidsRelacionadasComCiap.push(item)
        : outrasCid.push(item)
    })

    return {
      hasCidsRelacionadas: cidsRelacionadasComCiap.length > 1,
      hasOutrasCids: outrasCid.length > 1,
      items: cidsRelacionadasComCiap.concat(outrasCid),
    }
  }, [ciapRelacionada, items, renderGrupoCiapRelacionada])

  return (
    <SelectField<CidSelectFieldModel>
      itemToString={cidItemToString}
      items={selectItems.items}
      renderItem={cidRenderItem}
      dataTestId={dataTestId}
      components={{
        Item: (itemProps) => (
          <AntecedentesFamiliaresCidSelectItem
            blockedCidIds={blockedCidIds}
            itemIsDisabled={itemIsDisabled}
            hasCidsRelacionadas={selectItems.hasCidsRelacionadas}
            hasOutrasCids={selectItems.hasOutrasCids}
            ciapRelacionada={ciapRelacionada}
            itemProps={itemProps}
          />
        ),
      }}
      {...asyncPropsRest}
      {...rest}
      placeholder={placeholder}
    />
  )
}
